import { useContext, useEffect, useRef, useState } from "react";
import { methodsOf } from "../../src/booking.ts";
import { ApiError, api, type Item, type TripView } from "./api.ts";
import { type Line, newLine, type Problem, paymentOf } from "./bookingDraft.ts";
import { currencyList, PaymentFields, YourCurrency } from "./fields.tsx";
import { personName, weekday } from "./format.ts";
import { asLabel, everyone } from "./former.tsx";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { paidMoney } from "./paid.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon } from "./souvenir.ts";

// A payment on any card (DESIGN.md, Views, the web's own edits; features/bookings-and-costs.md, "Add a payment"):
// what's paid along the way, booked or not (a dinner, a taxi), the booking form's payment line on its own: who (me
// first), how much in what, and how. Added to its `paid`; a payment of mine is taken off on the same card. Charged
// later the same way ("Charged later", with its day: features/bookings-and-costs.md, "Charged later"), into its
// `charges`, one on my card taken off on the same card, and one confirmed as paid there by anyone once its day has
// come (Paid). Each one write signed "on the web", undoable at once (the trip's notice) and from Updates, over the
// item as read.

// The currencies to pick from: the item's own, the trip's, mine, then every other.
export function paymentCurrencies(item: Item, trip: TripView, items: Item[], yours: string | null): string[] {
  return currencyList(
    [
      item.cost?.currency,
      ...(item.paid ?? []).map((p) => p.currency),
      ...(item.charges ?? []).map((p) => p.currency),
      trip.money.estimate?.currency,
      trip.money.budget?.currency,
      yours,
      ...items.flatMap((i) => [i.cost?.currency, ...(i.paid ?? []).map((p) => p.currency)]),
    ].filter((c): c is string => Boolean(c)),
  );
}

export function AddPaymentForm({
  item,
  trip,
  items,
  onChanged,
  onClose,
}: {
  item: Item;
  trip: TripView;
  items: Item[];
  onChanged: () => void;
  // Closed, saved or not (saved: once the trip has loaded with it).
  onClose: () => void;
}) {
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const currencies = paymentCurrencies(item, trip, items, useContext(YourCurrency));
  // The item as the form read it: its version is what the change is written over.
  const [base, setBase] = useState(item);
  const [line, setLine] = useState<Line>(() =>
    newLine({ by: trip.you, amount: "", currency: currencies[0] ?? "USD", method: "" }),
  );
  const [problems, setProblems] = useState<Problem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [meanwhile, setMeanwhile] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<number | null>(null);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
    form.current?.scrollIntoView({ block: "nearest" });
  }, []);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (saved === null) return;
    if (item.version >= saved) return close.current();
    const timer = setTimeout(() => close.current(), 5000);
    return () => clearTimeout(timer);
  }, [saved, item.version]);

  const me = trip.members.find((m) => m.id === trip.you);
  const others = trip.members.filter((m) => m.id !== trip.you).sort((a, b) => a.name.localeCompare(b.name));
  const who = [
    ...(me ? [{ value: me.id, label: `You (${me.name})` }] : []),
    ...others.map((m) => ({ value: m.id, label: m.name })),
  ];
  const problem = (field: string) => problems.find((p) => p.field === field)?.message ?? null;

  const save = async () => {
    if (offline || busy || saved !== null) return;
    // A method the thing has already (an old one with digits, from before) is kept as it is.
    const made = paymentOf(line, new Set(methodsOf(base)));
    if (!made.payment || made.problems.length > 0) {
      setProblems(made.problems);
      const first = made.problems[0];
      if (first) form.current?.querySelector<HTMLElement>(`[data-field="${CSS.escape(first.field)}"]`)?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { on, ...payment } = made.payment;
      const { change, version } = await api.pay(
        trip.id,
        item.id,
        on ? { version: base.version, charge: { ...payment, on } } : { version: base.version, add: payment },
      );
      onChanged();
      const by = personName(payment.by, everyone(trip), trip.you);
      const money = paidMoney(payment.amount, payment.currency);
      say({
        change,
        text: on
          ? `${money} charged later, on ${weekday(on)}, for ${item.title}`
          : `${money} paid by ${by === "You" ? "you" : (by ?? "someone")} for ${item.title}`,
      });
      setSaved(version);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.status === 409 && e.data["item"]) {
        const fresh = e.data["item"] as Item;
        const by = fresh.updated?.by;
        setBase(fresh);
        setMeanwhile(by?.id === trip.you ? "You" : asLabel(by?.name ?? "Someone"));
        onChanged();
        return;
      }
      setError(e instanceof Error ? e.message : "Couldn't save the payment. Try again.");
    }
  };

  return (
    <form
      ref={form}
      className="tm-edit-form tm-add-payment"
      aria-labelledby={`pay-${item.id}`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <h2 id={`pay-${item.id}`} className="title">
        Add a payment
      </h2>
      {meanwhile && (
        <p className="callout tm-booking-note is-meanwhile" role="alert">
          <Icon name="people" size={20} className="tm-booking-icon" />
          <span>
            {meanwhile === "You" ? "You changed it elsewhere" : `${meanwhile} changed it`} while this was open, so
            nothing was saved. Save again to add yours.
          </span>
        </p>
      )}
      <PaymentFields
        line={line}
        n={1}
        who={who}
        currencies={currencies}
        problem={problem}
        onChange={(patch) => {
          setLine((l) => ({ ...l, ...patch }));
          const fields = Object.keys(patch).map((k) => `${line.key}.${k}`);
          setProblems((ps) => ps.filter((p) => !fields.includes(p.field)));
        }}
      />
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy || saved !== null || offline}>
          {busy || saved !== null ? "Saving…" : "Save"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={onClose}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

// A payment of mine (or a charge later on my card: `charge`) taken off its card: one tap, Undo at once in the trip's
// notice. `at`: its place in the list.
export function RemovePayment({
  item,
  trip,
  at,
  words,
  charge = false,
  onChanged,
}: {
  item: Item;
  trip: TripView;
  at: number;
  // The payment as its line says it, for its name.
  words: string;
  charge?: boolean;
  onChanged: () => void;
}) {
  const say = useJustDone();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      const { change } = await api.pay(
        trip.id,
        item.id,
        charge ? { version: item.version, remove_charge: at } : { version: item.version, remove: at },
      );
      onChanged();
      say({ change, text: `Removed a ${charge ? "charge" : "payment"} from ${item.title}` });
    } catch (e) {
      setBusy(false);
      setError(
        e instanceof ApiError && e.status === 409
          ? "It changed meanwhile, so nothing was removed. Look again and try again."
          : e instanceof Error
            ? e.message
            : "Couldn't remove it. Try again.",
      );
      if (e instanceof ApiError && e.status === 409) onChanged();
    }
  };
  return (
    <>
      <button
        type="button"
        className="tm-fact-remove"
        aria-label={`Remove the ${charge ? "charge" : "payment"}: ${words}`}
        disabled={busy || !online}
        onClick={remove}
      >
        <Icon name="close" size={16} />
      </button>
      {error && (
        <span className="caption tm-error tm-fact-error" role="alert">
          {error}
          <ReportThis />
        </span>
      )}
    </>
  );
}

// A charge later whose day has come (Details.tsx, its card), still to confirm: Paid, in one tap, moves it into what's
// paid (Undo at once in the trip's notice; Tripmo can't see a card being charged, so it never moves by itself); Edit
// opens the booking form where it's changed, on a card that has one. `at`: its place in the list. `words`: it as its
// line says it, for the buttons' names.
export function ChargeDue({
  item,
  trip,
  at,
  words,
  onChanged,
  onEdit,
}: {
  item: Item;
  trip: TripView;
  at: number;
  words: string;
  onChanged: () => void;
  onEdit?: (() => void) | undefined;
}) {
  const say = useJustDone();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    const charge = item.charges?.[at];
    if (!charge) return;
    setBusy(true);
    setError(null);
    try {
      const { change } = await api.pay(trip.id, item.id, { version: item.version, confirm: at });
      onChanged();
      say({ change, text: `${paidMoney(charge.amount, charge.currency)} paid, as charged, for ${item.title}` });
    } catch (e) {
      setBusy(false);
      setError(
        e instanceof ApiError && e.status === 409
          ? "It changed meanwhile, so nothing was saved. Look again and try again."
          : e instanceof Error
            ? e.message
            : "Couldn't save it. Try again.",
      );
      if (e instanceof ApiError && e.status === 409) onChanged();
    }
  };
  return (
    <span className="tm-fact-due">
      <Button
        variant="secondary"
        size="sm"
        icon="check"
        aria-label={`Paid: ${words}`}
        disabled={busy || !online}
        onClick={confirm}
      >
        {busy ? "Saving…" : "Paid"}
      </Button>
      {onEdit && (
        <Button variant="quiet" size="sm" aria-label={`Edit the charge: ${words}`} onClick={onEdit}>
          Edit
        </Button>
      )}
      {error && (
        <span className="caption tm-error tm-fact-error" role="alert">
          {error}
          <ReportThis />
        </span>
      )}
    </span>
  );
}
